// Medição de desempenho das telas, do lado do navegador.
//
// Junta duas coisas e manda para a edge function registrar-desempenho, que soma
// no agregado diário (public.desempenho_diario):
//
// - o carregamento da página (TTFB, FCP e LCP da tela em que ela abriu; CLS e
//   INP da tela em que a pessoa estava), medidos pelo próprio navegador com
//   PerformanceObserver, sem biblioteca;
// - a duração de cada chamada ao Supabase, por tela e por recurso (tabela, rpc
//   ou edge function), lida das entradas de "resource" do navegador. É o
//   número que diz se a tela é lenta pelo front ou pela consulta.
//
// Nada identifica a pessoa: a rota vai com os identificadores trocados por
// ":id", o recurso vai sem parâmetros, e a função não grava quem mandou.

import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const FUNCAO = 'registrar-desempenho';
/** A aba do ECO fica aberta o dia todo e quase nunca é escondida: envia também por tempo. */
const ENVIO_A_CADA_MS = 10 * 60_000;
const VALORES_POR_ITEM = 200;
/**
 * Limites da função (80 itens e 5.000 valores por chamada) e do keepalive: o
 * navegador recusa quando a soma dos envios keepalive em andamento passa de
 * 64 KB, por isso o envio da saída de vista é um lote só, menor.
 */
const BYTES_POR_ENVIO = 60_000;
const BYTES_NA_SAIDA = 30_000;
const ITENS_POR_ENVIO = 80;
const VALORES_POR_ENVIO = 5_000;

export type ItemDesempenho = {
  tipo: 'carga' | 'consulta';
  rota: string;
  item: string;
  valores: number[];
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Rota sem identificador: `/clientes/3f2a…/editar` vira `/clientes/:id/editar`.
 * Número, uuid, pedaço longo com dígito (token de link público) e pedaço com
 * quatro dígitos seguidos viram ":id"; o resto vai em minúsculas e sem acento,
 * até 5 níveis.
 */
export function normalizarRota(caminho: string): string {
  const partes = caminho
    .split('?')[0]
    .split('#')[0]
    .split('/')
    .filter(Boolean)
    .slice(0, 5)
    .map((p) => {
      let s = p;
      try { s = decodeURIComponent(p); } catch { /* fica como veio */ }
      if (/^\d+$/.test(s) || UUID.test(s) || (s.length >= 16 && /\d/.test(s))) return ':id';
      const limpo = s
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9\-_]/g, '');
      // Quatro dígitos seguidos depois da limpeza podem ser documento
      // (123.456.789-00 vira 123456789-00); a função recusa a rota se passar.
      if (/\d{4,}/.test(limpo)) return ':id';
      return limpo.slice(0, 40) || ':id';
    });
  return ('/' + partes.join('/')).slice(0, 120);
}

/**
 * Recurso do Supabase a partir da URL da chamada, sem parâmetros:
 * `rest:clientes`, `rpc:minha_funcao`, `fn:gclick-proxy`, `storage:bucket`.
 * Devolve null para o que não é chamada de dado (auth, realtime, outra origem)
 * e para a própria medição, que não deve se medir.
 */
export function classificarRecurso(url: string, base = SUPABASE_URL): string | null {
  if (!base || !url.startsWith(base)) return null;
  const caminho = url.slice(base.length).split('?')[0];
  const nome = (s: string | undefined) => (s || '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 80);
  let m = caminho.match(/^\/rest\/v1\/rpc\/([^/]+)/);
  if (m) return nome(m[1]) ? `rpc:${nome(m[1])}` : null;
  m = caminho.match(/^\/rest\/v1\/([^/]+)/);
  if (m) return nome(m[1]) ? `rest:${nome(m[1])}` : null;
  m = caminho.match(/^\/functions\/v1\/([^/]+)/);
  if (m) {
    const fn = nome(m[1]);
    return fn && fn !== FUNCAO ? `fn:${fn}` : null;
  }
  m = caminho.match(/^\/storage\/v1\/object\/(?:public\/|sign\/|authenticated\/)?([^/]+)/);
  if (m) return nome(m[1]) ? `storage:${nome(m[1])}` : null;
  return null;
}

/** Junta valores por (tipo, rota, item) até o envio. */
export class Acumulador {
  private mapa = new Map<string, ItemDesempenho>();

  adicionar(tipo: ItemDesempenho['tipo'], rota: string, item: string, valor: number) {
    if (!Number.isFinite(valor) || valor < 0) return;
    const chave = `${tipo}|${rota}|${item}`;
    let atual = this.mapa.get(chave);
    if (!atual) {
      atual = { tipo, rota, item, valores: [] };
      this.mapa.set(chave, atual);
    }
    if (atual.valores.length < VALORES_POR_ITEM) atual.valores.push(Math.round(valor));
  }

  vazio() {
    return this.mapa.size === 0;
  }

  /** Entrega o que juntou e começa de novo. */
  retirar(): ItemDesempenho[] {
    const itens = [...this.mapa.values()];
    this.mapa.clear();
    return itens;
  }
}

/** Corpos JSON dentro dos limites da função e do tamanho pedido. */
export function dividirEmEnvios(itens: ItemDesempenho[], bytes = BYTES_POR_ENVIO): string[] {
  const envios: string[] = [];
  let lote: ItemDesempenho[] = [];
  let tamanho = 12;
  let valores = 0;
  for (const item of itens) {
    const t = JSON.stringify(item).length + 1;
    if (lote.length && (tamanho + t > bytes || lote.length >= ITENS_POR_ENVIO || valores + item.valores.length > VALORES_POR_ENVIO)) {
      envios.push(JSON.stringify({ itens: lote }));
      lote = [];
      tamanho = 12;
      valores = 0;
    }
    lote.push(item);
    tamanho += t;
    valores += item.valores.length;
  }
  if (lote.length) envios.push(JSON.stringify({ itens: lote }));
  return envios;
}

let iniciado = false;

/**
 * Liga a medição. Chamada uma vez, no main.tsx. Em navegador sem
 * PerformanceObserver (ou nos testes) não faz nada.
 */
export function iniciarMedicaoDeDesempenho() {
  if (iniciado || typeof window === 'undefined' || typeof PerformanceObserver === 'undefined' || !SUPABASE_URL) return;
  iniciado = true;

  const acumulador = new Acumulador();
  const rotaAtual = () => normalizarRota(window.location.pathname);
  const rotaInicial = rotaAtual();
  const suportados = PerformanceObserver.supportedEntryTypes ?? [];
  const observar = (tipo: string, cb: (lista: PerformanceEntryList) => void, extra: Record<string, unknown> = {}) => {
    if (!suportados.includes(tipo)) return;
    try {
      new PerformanceObserver((l) => cb(l.getEntries())).observe({ type: tipo, buffered: true, ...extra } as PerformanceObserverInit);
    } catch { /* navegador sem esse tipo */ }
  };

  // TTFB e FCP: uma vez, da tela em que a página abriu.
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  if (nav && nav.responseStart > 0) acumulador.adicionar('carga', rotaInicial, 'ttfb', nav.responseStart);
  observar('paint', (entradas) => {
    for (const e of entradas) if (e.name === 'first-contentful-paint') acumulador.adicionar('carga', rotaInicial, 'fcp', e.startTime);
  });

  // LCP: o último candidato antes da primeira interação ou de a aba sair de vista.
  let lcp = 0;
  let lcpFechado = false;
  observar('largest-contentful-paint', (entradas) => {
    if (lcpFechado) return;
    const ultima = entradas[entradas.length - 1];
    if (ultima) lcp = ultima.startTime;
  });
  const fecharLcp = () => {
    if (lcpFechado) return;
    lcpFechado = true;
    if (lcp > 0) acumulador.adicionar('carga', rotaInicial, 'lcp', lcp);
  };
  for (const ev of ['keydown', 'pointerdown']) addEventListener(ev, fecharLcp, { once: true, capture: true });

  // Em que tela a pessoa estava em cada momento. O roteador troca a URL sem
  // avisar ninguém, então a rota é conferida a cada meio segundo, no começo de
  // cada interação e no voltar do navegador. É o que permite pôr a interação e
  // a chamada na tela em que começaram, e não na tela em que estavam no envio.
  const historico: { desde: number; rota: string }[] = [{ desde: 0, rota: rotaInicial }];
  const rotaEm = (t: number) => {
    for (let i = historico.length - 1; i >= 0; i--) if (historico[i].desde <= t) return historico[i].rota;
    return historico[0].rota;
  };
  const conferirRota = (agora = performance.now()) => {
    const rota = rotaAtual();
    if (rota === historico[historico.length - 1].rota) return;
    fecharVisita();
    historico.push({ desde: agora, rota });
    if (historico.length > 50) historico.splice(0, historico.length - 50);
  };
  for (const ev of ['pointerdown', 'keydown']) {
    addEventListener(ev, (e) => conferirRota(e.timeStamp), { capture: true, passive: true });
  }
  addEventListener('popstate', () => conferirRota());
  setInterval(() => conferirRota(), 500);

  // CLS por visita a uma tela: a maior janela de sessão (até 5 s, com pausas de
  // menos de 1 s), como o Web Vitals. Visita sem deslocamento conta como zero,
  // senão o p75 sairia só das visitas ruins. A visita fecha na troca de tela e
  // quando a aba sai de vista. Vai em milésimos.
  let clsMaior = 0;
  let clsJanela = 0;
  let clsInicio = 0;
  let clsUltimo = 0;
  function fecharVisita() {
    const rota = historico[historico.length - 1].rota;
    // Sem layout-shift (Firefox, Safari) não há medição, e zero seria mentira.
    if (suportados.includes('layout-shift')) acumulador.adicionar('carga', rota, 'cls', Math.max(clsMaior, clsJanela) * 1000);
    clsMaior = clsJanela = clsInicio = clsUltimo = 0;
  }
  observar('layout-shift', (entradas) => {
    conferirRota();
    for (const e of entradas as (PerformanceEntry & { value: number; hadRecentInput: boolean })[]) {
      if (e.hadRecentInput) continue;
      if (clsJanela && (e.startTime - clsUltimo > 1000 || e.startTime - clsInicio > 5000)) {
        clsMaior = Math.max(clsMaior, clsJanela);
        clsJanela = 0;
      }
      if (!clsJanela) clsInicio = e.startTime;
      clsJanela += e.value;
      clsUltimo = e.startTime;
    }
  });

  // INP: cada interação conta como amostra da tela em que começou; o p75 do
  // agregado faz o papel do "pior típico" do Web Vitals. O navegador só avisa
  // interação de 16 ms ou mais, então as instantâneas ficam de fora e o número
  // sai um pouco pior que o real.
  const interacoes = new Map<number, { duracao: number; rota: string }>();
  observar('event', (entradas) => {
    for (const e of entradas as (PerformanceEntry & { interactionId?: number })[]) {
      if (!e.interactionId) continue;
      const anterior = interacoes.get(e.interactionId);
      if (!anterior || e.duration > anterior.duracao) {
        interacoes.set(e.interactionId, { duracao: e.duration, rota: anterior?.rota ?? rotaEm(e.startTime) });
      }
    }
  }, { durationThreshold: 16 });
  const fecharInteracoes = () => {
    for (const { duracao, rota } of interacoes.values()) acumulador.adicionar('carga', rota, 'inp', duracao);
    interacoes.clear();
  };

  // Chamadas ao Supabase, na tela em que começaram.
  observar('resource', (entradas) => {
    for (const e of entradas as PerformanceResourceTiming[]) {
      if (e.initiatorType !== 'fetch' && e.initiatorType !== 'xmlhttprequest') continue;
      const recurso = classificarRecurso(e.name);
      if (recurso && e.duration > 0) acumulador.adicionar('consulta', rotaEm(e.startTime), recurso, e.duration);
    }
  });

  // Token guardado de antemão: na hora de esconder a aba não dá para esperar.
  let token: string | null = null;
  supabase.auth.getSession().then(({ data }) => { token = data.session?.access_token ?? null; }).catch(() => {});
  supabase.auth.onAuthStateChange((_ev, sessao) => { token = sessao?.access_token ?? null; });

  const enviar = (saindo: boolean) => {
    fecharLcp();
    // A visita só termina quando a aba sai de vista; o envio por tempo leva o
    // que já fechou e deixa a visita aberta seguir somando.
    if (saindo) fecharVisita();
    fecharInteracoes();
    if (acumulador.vazio()) return;
    const itens = acumulador.retirar();
    // Sem sessão (tela pública, login) a função recusaria: descarta.
    if (!token) return;
    // Saindo de vista, só o keepalive garante a entrega, e ele aceita pouco:
    // vai o primeiro lote, o resto se perde. Com a aba aberta, vão todos.
    const envios = saindo ? dividirEmEnvios(itens, BYTES_NA_SAIDA).slice(0, 1) : dividirEmEnvios(itens);
    for (const corpo of envios) {
      fetch(`${SUPABASE_URL}/functions/v1/${FUNCAO}`, {
        method: 'POST',
        keepalive: saindo,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
        },
        body: corpo,
      }).catch(() => { /* medição não pode atrapalhar a tela */ });
    }
  };

  addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') enviar(true);
  });
  setInterval(() => enviar(false), ENVIO_A_CADA_MS);
}
